/**
 * Chat error mapping + module-navigation / tour-chip helpers for useChat.
 */

import type { QuickAction } from '../hooks/useChat.types'

/**
 * Detect if a user message is a simple module navigation command
 * (e.g., "show calendar", "open my contacts", "show paris restaurants").
 * These are ephemeral navigation actions, not real conversation turns.
 */
const MODULE_NAV_PATTERN = /^\s*(show|open|display|view|go to|switch to)\s+(my\s+)?/i

export function isModuleNavigationMessage(content: string): boolean {
  const trimmed = (content || '').trim()
  if (!MODULE_NAV_PATTERN.test(trimmed)) return false
  // Must be a short command, not a real question or conversation.
  return trimmed.length < 80 && !trimmed.includes('?')
}

/**
 * Tour-chip fallback map. The agent is supposed to emit the next-step
 * chip via a `show_actions` tool call, but the model skips that call
 * roughly half the time (text + focus_module is enough to look complete
 * to the LLM). Without the chip the user is stranded mid-tour, so the
 * frontend injects the next chip when:
 *   - the user's last message exactly matches a tour-chip message, AND
 *   - the agent emitted a focus_module ui_action, AND
 *   - the agent emitted no chips of its own.
 *
 * Keys are lowercased, trimmed user-message text. Order mirrors
 * BOOTSTRAP.md: Email → Calendar → Drive → Tasks → Places → Pages →
 * Contacts → done.
 */
interface TourFallback {
  /** Chip to inject so the user can advance to the next step. */
  chip: QuickAction
  /** Module type the agent should have focused but might have skipped. */
  moduleType: string
}

export const TOUR_CHIP_FALLBACK: Record<string, TourFallback> = {
  'show me calendar': {
    chip: { label: 'Show me Drive', message: 'Show me drive', kind: 'drive.tour', variant: 'primary' },
    moduleType: 'event_list',
  },
  'show me drive': {
    chip: { label: 'Show me Kanban', message: 'Show me kanban', kind: 'tasks.tour', variant: 'primary' },
    moduleType: 'drive',
  },
  'show me tasks': {
    chip: { label: 'Show me Places', message: 'Show me places', kind: 'places.tour', variant: 'primary' },
    moduleType: 'task_list',
  },
  'show me places': {
    chip: { label: 'Show me Studio', message: 'Show me pages', kind: 'pages.tour', variant: 'primary' },
    moduleType: 'places',
  },
  // Studio (Pages) is the final step — its chip ends the tour by closing
  // the open module panel via the tour.end kind handler in ChatBox.
  'show me pages': {
    chip: { label: "I'm good — let me explore", message: 'Done with tour', kind: 'tour.end' },
    moduleType: 'pages',
  },
}

export function getUserFriendlyErrorMessage(
  error: Error & {
    status?: number
    resetAt?: string
    errorCode?: string
    retryAfterSec?: number
  },
): Error {
  const message = error.message || ''
  const status = error.status

  // Rate limit (429) — daily token budget exceeded
  if (status === 429 || message.includes('daily') || message.includes('rate limit')) {
    const err = new Error("You've reached your daily usage limit. Please upgrade for higher limits.") as Error & {
      status?: number
      upgradeRequired?: boolean
      resetsAt?: string
      errorCode?: string
      retryAfterSec?: number
    }
    err.status = 429
    err.upgradeRequired = true
    if (error.resetAt) err.resetsAt = error.resetAt
    if (error.errorCode) err.errorCode = error.errorCode
    if (error.retryAfterSec) err.retryAfterSec = error.retryAfterSec
    return err
  }

  // Payment required (402) — plan limit reached or no active subscription
  if (status === 402) {
    const err = new Error('Upgrade to continue using XAVIOR.') as Error & {
      status?: number
      upgradeRequired?: boolean
      errorCode?: string
    }
    err.status = 402
    err.upgradeRequired = true
    err.errorCode = 'PAYMENT_REQUIRED'
    return err
  }

  // Intent denied (403) — tier-gated model/feature access
  if (status === 403 && error.errorCode === 'INTENT_DENIED') {
    const err = new Error('This feature requires a paid plan.') as Error & {
      status?: number
      upgradeRequired?: boolean
      errorCode?: string
    }
    err.status = 403
    err.upgradeRequired = true
    err.errorCode = 'INTENT_DENIED'
    return err
  }

  // Payload too large (413) — the accumulated conversation/attachments pushed
  // the request body past x-agent's ceiling (#5548). x-agent returns a JSON
  // body whose `message` already reads "start a new chat"; fall back to a
  // literal here for the raw-413 case (older server / gateway HTML page).
  if (status === 413 || message.includes('413') || message.toLowerCase().includes('too large')) {
    const friendly = message.toLowerCase().includes('start a new chat')
      ? message
      : 'This message is too large to send. Start a new chat to continue.'
    const err = new Error(friendly) as Error & { status?: number }
    err.status = 413
    return err
  }

  // SSE/connection failures
  if (message.includes('SSE request failed: 503') || message.includes('503')) {
    return new Error('The chat service is temporarily unavailable. Please try again in a moment.')
  }
  if (message.includes('SSE request failed: 502') || message.includes('502')) {
    return new Error('The chat service encountered an issue. Please try again.')
  }
  if (message.includes('SSE request failed: 504') || message.includes('504')) {
    return new Error('The request took too long. Please try again.')
  }
  if (message.includes('SSE request failed')) {
    return new Error('Unable to connect to the chat service. Please check your connection and try again.')
  }

  // Network errors (case-insensitive for cross-browser: Safari uses 'network error', Chrome uses 'Failed to fetch')
  const lower = message.toLowerCase()
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('network error') || lower.includes('network request failed')) {
    return new Error('Network connection lost. Please check your internet connection.')
  }

  // Keep original error if no transformation needed
  return error
}
